if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        // If there is no thumbnail gallery, stop here
        if (!this.elements.thumbnails) return;

        // Existing slide functionality
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        // Existing thumbnail click functionality
        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
          });

        if (
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        /*
         * NEW:
         * Listen for Shopify variant/color changes.
         */
        this.colorChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleColorChange.bind(this)
        );
      }

      /*
       * Clean up the event listener when the gallery is removed.
       */
      disconnectedCallback() {
        if (this.colorChangeUnsubscriber) {
          this.colorChangeUnsubscriber();
        }
      }

      /*
       * Existing slide change function
       */
      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      /*
       * NEW:
       * Runs whenever a product option changes.
       *
       * We only react when the option is "Color".
       */
      handleColorChange(event) {
        const data = event?.data;
        const target = data?.target;

        if (!target) return;

        const optionName = (
          target.dataset.optionName || ''
        )
          .trim()
          .toLowerCase();

        // Only filter when the selected option is Color
        if (optionName !== 'color') return;

        const selectedColor = (
          target.value || ''
        )
          .trim()
          .toLowerCase();

        if (!selectedColor) return;

        /*
         * Get all product media.
         */
        const mediaItems =
          this.elements.viewer.querySelectorAll(
            '[data-media-id]'
          );

        let firstMatchingMedia = null;

        /*
         * Show only images whose ALT text
         * exactly matches the selected color.
         *
         * Example:
         * Blue  -> blue
         * Black -> black
         * Yellow -> yellow
         * Olive -> olive
         */
        mediaItems.forEach((media) => {
          const image = media.querySelector('img');

          if (!image) return;

          const altText = (
            image.alt || ''
          )
            .trim()
            .toLowerCase();

          const matches = altText === selectedColor;

          if (matches) {
            media.style.display = '';

            if (!firstMatchingMedia) {
              firstMatchingMedia = media;
            }
          } else {
            media.style.display = 'none';
          }
        });

        /*
         * Do the same thing for thumbnails.
         */
        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((thumbnail) => {
              const mediaId = thumbnail.dataset.target;

              const media =
                this.elements.viewer.querySelector(
                  `[data-media-id="${mediaId}"]`
                );

              if (!media) return;

              if (media.style.display === 'none') {
                thumbnail.style.display = 'none';
              } else {
                thumbnail.style.display = '';
              }
            });
        }

        /*
         * Automatically show the first image
         * belonging to the selected color.
         */
        if (firstMatchingMedia) {
          this.setActiveMedia(
            firstMatchingMedia.dataset.mediaId,
            false
          );
        }
      }

      /*
       * Existing active media function
       */
      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        if (!activeMedia) {
          return;
        }

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !==
                activeThumbnail &&
                activeThumbnail.parentElement.prepend(
                  activeThumbnail
                );
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (
            !this.mql.matches ||
            this.elements.thumbnails
          ) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;

          const top =
            activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      /*
       * Existing thumbnail function
       */
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
          });

        const button = thumbnail.querySelector('button');

        if (button) {
          button.setAttribute('aria-current', true);
        }

        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      /*
       * Existing accessibility function
       */
      announceLiveRegion(activeItem, position) {
        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      /*
       * Existing video/model function
       */
      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      /*
       * Existing sticky header function
       */
      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      /*
       * Existing accessibility function
       */
      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
        );
      }
    }
  );
}